import React, { ReactNode, useContext, useState } from 'react';
import classNames from 'classnames';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { paths } from '@starlingbank/shared';

import { trackGtm } from '../../utils';
import { MenuDropDowns, SiteNavigationContext } from './SiteNavigation';
import * as classes from './MegaMenu.module.scss';

import iconPca from './assets/nav-icons/pca.svg';
import iconJca from './assets/nav-icons/jca.svg';
import iconEuro from './assets/nav-icons/euro.svg';
import iconTeen from './assets/nav-icons/teen.svg';
import iconKite from './assets/nav-icons/kite.svg';

import iconBca from './assets/nav-icons/bca.svg';
import iconSta from './assets/nav-icons/sta.svg';
import iconMultiCurrency from './assets/nav-icons/multi-currency.svg';

import onlineBanking from './assets/nav-icons/log-in-online-banking.svg';
import accordionIcon from './assets/accordion-icon.svg';

type ActiveMenuItem = Extract<MenuDropDowns, 'pca' | 'bca'>;

type MegaMenuProps = {
  activeMenu?: MenuDropDowns;
  updateMegaMenu: (selecton: MenuDropDowns) => void;
};

type TextLinkProps = {
  href: string;
  target?: string;
  withIcon?: boolean;
  className?: string;
  trackingLabel: string;
  children: string | ReactNode;
};

const ICONS = {
  iconPca,
  iconJca,
  iconEuro,
  iconTeen,
  iconKite,
  iconBca,
  iconSta,
  iconMultiCurrency
};

const TextLink = ({ withIcon = false, className, trackingLabel, href, children, ...props }: TextLinkProps) => {
  const { activeMenu } = useContext(SiteNavigationContext);

  const track = () =>
    trackGtm({
      event: 'site_nav_click',
      parent: activeMenu,
      linkLabel: trackingLabel,
      linkUrl: href
    });

  return (
    <NavigationMenu.Link
      href={href}
      className={classNames(
        {
          [classes.linkBase]: withIcon,
          [classes.textLink]: !withIcon
        },
        className
      )}
      onClick={track}
      onSelect={(e) => e.preventDefault()}
      {...props}
    >
      {children}
    </NavigationMenu.Link>
  );
};

const AccordionItem = ({
  activeMenu,
  i,
  heading,
  links
}: {
  activeMenu: ActiveMenuItem;
  i: number;
  heading: string;
  links: paths.NavLink[];
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      key={heading}
      className={classNames(classes[`linkRegion${i + 1}`], {
        [classes.bcaTheme]: activeMenu === 'bca'
      })}
    >
      <button className={classes.sectionHeading} onClick={() => setIsOpen(!isOpen)}>
        {heading}
        <img
          src={accordionIcon}
          width={48}
          height={48}
          alt={`Toggle the ${heading} section`}
          className={classNames(classes.accordionIcon, {
            [classes.accordionIconOpen]: isOpen
          })}
        />
      </button>
      <NavigationMenu.List className={classNames(classes.list, { [classes.accordionListClosed]: !isOpen })}>
        {links.map((link) => (
          <NavigationMenu.Item key={link.text}>
            <TextLink href={link.href} trackingLabel={link.text}>
              {link.text}
            </TextLink>
          </NavigationMenu.Item>
        ))}
      </NavigationMenu.List>
    </div>
  );
};

export const MegaMenu = ({ activeMenu, updateMegaMenu }: MegaMenuProps): JSX.Element => {
  const data = paths.siteNavigation[activeMenu as Extract<MenuDropDowns, 'pca' | 'bca'>];

  const toggleTabs = (accountType: Extract<MenuDropDowns, 'pca' | 'bca'>) => {
    updateMegaMenu(accountType);
  };

  return (
    <div className={classNames('sn-mm', classes.container)}>
      <nav className={classes.navMegaMenuBase}>
        <div className={classes.intro}>
          <NavigationMenu.List className={classes.accountButtonContainer}>
            <NavigationMenu.Item className={classes.accountTabListItem} value="pca">
              <NavigationMenu.Trigger
                className={classNames(classes.accountButton, {
                  [classes.pcaInactive]: activeMenu !== 'pca'
                })}
                onPointerMove={(e) => e.preventDefault()}
                onPointerLeave={(e) => e.preventDefault()}
                onClick={() => toggleTabs('pca')}
              >
                Personal
              </NavigationMenu.Trigger>
            </NavigationMenu.Item>
            <NavigationMenu.Item className={classes.accountTabListItem} value="bca">
              <NavigationMenu.Trigger
                className={classNames(classes.accountButton, {
                  [classes.bca]: activeMenu === 'bca',
                  [classes.bcaInactive]: activeMenu !== 'bca'
                })}
                onPointerMove={(e) => e.preventDefault()}
                onPointerLeave={(e) => e.preventDefault()}
                onClick={() => toggleTabs('bca')}
              >
                Business
              </NavigationMenu.Trigger>
            </NavigationMenu.Item>
          </NavigationMenu.List>
          <p className={classes.introHeading}>{data.intro[0]}</p>
          <p className={classes.introText}>{data.intro[1]}</p>
        </div>

        <div className={classes.footerLinksContainer}>
          {activeMenu === 'pca' && (
            <div className={classNames(classes.loginSection, classes.pca)}>
              <TextLink
                href="https://app.starlingbank.com/"
                target="_blank"
                withIcon
                className={classes.loginSectionLink}
                trackingLabel="Log in to Online Banking"
              >
                <img src={onlineBanking} width={36} height={36} alt="Online banking icon" />
                Log in to Online Banking
              </TextLink>
            </div>
          )}
          <div className={classes.footerLinks}>
            <div className={classes.footerLinksPrimary}>
              <NavigationMenu.List className={classes.list}>
                {data.footerLinks.one.map((link) => (
                  <NavigationMenu.Item key={link.text}>
                    <TextLink href={link.href} trackingLabel={link.text}>
                      {link.text}
                    </TextLink>
                  </NavigationMenu.Item>
                ))}
              </NavigationMenu.List>
            </div>
            <div className={classes.footerLinksSecondary}>
              <div className={classes.secondaryItemContainer}>
                <NavigationMenu.List className={classes.list}>
                  {data.footerLinks.two.map((link) => (
                    <NavigationMenu.Item key={link.text}>
                      <TextLink href={link.href} trackingLabel={link.text}>
                        {link.text}
                      </TextLink>
                    </NavigationMenu.Item>
                  ))}
                </NavigationMenu.List>
              </div>
              <div className={classes.secondaryItemContainer}>
                <NavigationMenu.List className={classes.list}>
                  {data.footerLinks.three.map((link) => (
                    <NavigationMenu.Item key={link.text}>
                      <TextLink href={link.href} trackingLabel={link.text}>
                        {link.text}
                      </TextLink>
                    </NavigationMenu.Item>
                  ))}
                </NavigationMenu.List>
              </div>
              <div className={classes.secondaryItemContainer}>
                <NavigationMenu.List className={classes.list}>
                  {data.footerLinks.four.map((link) => (
                    <NavigationMenu.Item key={link.text}>
                      <TextLink href={link.href} trackingLabel={link.text}>
                        {link.text}
                      </TextLink>
                    </NavigationMenu.Item>
                  ))}
                  <NavigationMenu.Item className={classes.onlineBankLinkItem}>
                    <TextLink
                      href="https://app.starlingbank.com/"
                      target="_blank"
                      className={classes.loginSectionLink}
                      trackingLabel="Log in to Online Banking"
                    >
                      Log in to Online Banking
                    </TextLink>
                  </NavigationMenu.Item>
                </NavigationMenu.List>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.productLinks}>
          <NavigationMenu.List className={classes.productList}>
            {data.productLinks.map((link) => (
              <NavigationMenu.Item key={link.text}>
                <TextLink href={link.href} withIcon className={classes.productListLink} trackingLabel={link.text}>
                  <img src={ICONS[link.icon as keyof typeof ICONS]} width={36} height={36} alt="Account logo" />
                  {link.text}
                </TextLink>
              </NavigationMenu.Item>
            ))}
          </NavigationMenu.List>
          {activeMenu === 'bca' && (
            <div className={classNames(classes.loginSection, classes.bca)}>
              <TextLink
                href="https://app.starlingbank.com/"
                target="_blank"
                withIcon
                className={classes.loginSectionLink}
                trackingLabel="Log in to Online Banking"
              >
                <img src={onlineBanking} width={36} height={36} alt="Online banking icon" />
                Log in to Online Banking
              </TextLink>
            </div>
          )}
        </div>

        {data.linkSections.map((section, i) => (
          <AccordionItem
            key={section.heading}
            i={i}
            heading={section.heading}
            links={section.links}
            activeMenu={activeMenu as ActiveMenuItem}
          />
        ))}
      </nav>
    </div>
  );
};
